import {useCallback, useEffect, useReducer, useRef} from "react";

const initialState = {
    pages: {},  // store an array of items for each page
    itemsCount: 0,  // the total number of items
    isInitialized: false,  // whether the hook is initialized
}

const emptyPage = {
    items: [],
    loading: false,
}

/**
 * This hook provides the methods and the state to load a new page, cache the result and check whether a
 * page is loading.
 *
 * The getPageItems is supposed to accept a params object with the attribute page and pageSize, and it
 * is supposed to return an object with the list of items in the page and the overall number of items
 * (the number of items in all the pages).
 *
 * The hook is automatically re-initialized whenever the getPageItems changes.
 *
 * @param getPageItems an async function that given the page and the page size as argument return an object
 *   {pageItems: [...], itemsCount: int}.
 * @param pageSize default page size.
 * @param initializeOnMount if true it initialize the hook as soon as the component is mounted, otherwise
 *   the component has to be explicitly initialized or it is initialized whenever the getPageItems change.
 * @param initialPageNumber the initial page number (default is 1)
 */
const usePagination = ({getPageItems, pageSize = 20, initializeOnMount=true, initialPageNumber=1}) => {
    const [state, dispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'createPage':
                    return {
                        ...state,
                        pages: {
                            ...state.pages,
                            [action.pageNumber]: {...emptyPage, ...action.attributes}
                        }
                    }
                case 'updatePage':
                    return {
                        ...state,
                        pages: {
                            ...state.pages,
                            [action.pageNumber]: {...state.pages[action.pageNumber], ...action.pageItems}
                        }
                    }
                case 'updateItem':
                    const item = action.item;
                    const entry = Object.entries(state.pages).find(
                        ([_, page]) => !!page.items.find(i => i.id === item.id)
                    );
                    if(!!entry){
                        const [pageNumber, page] = entry;
                        const updatedPage = {...page, items: page.items.map(i => i.id === item.id ? {...i, ...item} : i)};  // It assumes that an item is a js object
                        return {...state, pages: {...state.pages, [pageNumber]: updatedPage}};
                    }
                    return state;
                default:
                    throw new Error(`Cannot find action ${action.type} for usePagination reducer`);
            }
        },
        initialState,
    )

    const mounted = useRef(false);

    /**
     * Fetch the items of a new pages and the overall (among all pages) number of items.
     * return an object {pageItems: [...], itemsCount: int}
     */
    const fetchPage = useCallback(
        async (pageNumber) => {
            const data = await getPageItems({"page_number": pageNumber, "page_size": pageSize});
            if (!Array.isArray(data?.pageItems)) {
                throw Error(`Expected pageItems to be an array of items, got ${data?.pageItems}`);
            }
            if (!Number.isInteger(data?.itemsCount)) {
                throw Error(`Expected itemsCount to be an integer, got ${data?.itemsCount}`);
            }
            return data;
        },
        [getPageItems, pageSize]
    )

    /**
     * Load the first page and get the number of items. Delete all the previously loaded pages.
     */
    const initialize = useCallback(
        async () => {
            dispatcher({type: "setAttributes", attributes: initialState})
            const {pageItems, itemsCount} = await fetchPage(initialPageNumber);
            dispatcher({
                type: "setAttributes", attributes: {
                    ...initialState,
                    itemsCount: itemsCount,
                    pages: {[initialPageNumber]: {items: pageItems, loading: false}},
                    isInitialized: true,
                }
            })
        },
        [fetchPage]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    /**
     * Load the items of a given page
     */
    const loadPage = useCallback(
        async (pageNumber) => {
            if (!state.pages[pageNumber]) {
                dispatcher({type: "createPage", pageNumber: pageNumber, attributes: {loading: true}});
                const {pageItems} = await fetchPage(pageNumber);
                dispatcher({
                    type: "updatePage",
                    pageNumber: pageNumber,
                    pageItems: {items: pageItems, loading: false},
                });
            }
        },
        [state.pages, fetchPage]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    /**
     * Given an item looks up in all the pages to find it and update it.
     * This is inefficient when the number of loaded page is large because it sequentially scans all the pages and items.
     * To check for equality between items the attribute id of the item is used.
     *
     * It assumes that each item has an id attribute.
     */
    const updateItem = useCallback(
        (item) => dispatcher({type: "updateItem", item: item}),
        [state.pages]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const isPageLoading = useCallback(
        (pageNumber) => {
            return state.pages[pageNumber] ? state.pages[pageNumber].loading : true;
        },
        [state.pages]
    )

    const pagesCount = Math.ceil(state.itemsCount / pageSize);

    /**
     * Initialize the hook whenever the initialize method change.
     * The component is initialized on the first rendering only if the flag
     * initializeOnMount is true.
     */
    useEffect(
        () => {
            if(initializeOnMount || mounted.current){
                initialize();
            }
            mounted.current = true;
        },
        [initialize, initializeOnMount]
    )

    return {
        pages: state.pages,
        itemsCount: state.itemsCount,
        isInitialized: state.isInitialized,
        initialPageNumber,
        pagesCount,
        initialize,
        loadPage,
        isPageLoading,
        pageSize,
        updateItem,
    }
}

export default usePagination;